import { useEffect, useCallback, useMemo, useState } from 'react';

import debounce from 'lodash-es/debounce';

import Avatar from '@atlaskit/avatar';
import { CSSObjectWithLabel } from '@atlaskit/react-select';
import { gridSize } from '@atlaskit/theme';

import SelectField from 'src/components/select-field';
import { Project } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { useParams } from 'src/router/hooks';
import { ProjectOption } from 'src/sections/dashboard/types';
import * as filterStyles from 'src/sections/repository/styles/filters';
import { selectControlStyles } from 'src/styles/select';

import * as styles from './project-filter.style';
import { useWorkspaceProjectFilter } from './projects-filter-provider';
import messages from './projects-filter.i18n';

const SHORT_PROJECT_SELECTED_WIDTH = gridSize() * 20;
const MEDIUM_PROJECT_SELECTED_WIDTH = gridSize() * 24;
const LONG_PROJECT_SELECTED_WIDTH = gridSize() * 32;

export const FILTER_DEBOUNCE_INTERVAL = 250;

type Props = {
  projectUuid: string | undefined;
  onChange: (uuid: string | undefined) => void;
};

export const ProjectsFilter = (props: Props): JSX.Element => {
  const [
    { currentProject, projects, loading, error },
    { setCurrentProject, fetchProjects, fetchCurrentProject },
  ] = useWorkspaceProjectFilter();

  const { projectUuid, onChange } = props;

  const [search, setSearch] = useState('');
  const [debounceSearch, setDebounceSearch] = useState('');

  const delayedDebounceSearch = useMemo(
    () => debounce(setDebounceSearch, FILTER_DEBOUNCE_INTERVAL),
    [setDebounceSearch]
  );

  const { formatMessage } = useIntl();

  const mapFilterOption = (project: Project | null): ProjectOption | null => {
    return project
      ? {
          label: project.name,
          value: project,
        }
      : null;
  };

  const formatOptionLabel = (option: ProjectOption) => {
    const { label, value } = option;
    return (
      <styles.ProjectOption>
        <Avatar
          src={value.links ? value.links.avatar.href : undefined}
          size="xsmall"
        />
        <filterStyles.Ellipsis>{label}</filterStyles.Ellipsis>
      </styles.ProjectOption>
    );
  };

  const clearSearch = () => {
    setSearch('');
    setDebounceSearch('');
  };

  const handleProjectChange = (option: ProjectOption) => {
    if (option && option.value) {
      setCurrentProject(option);
      onChange(option.value.uuid);
    } else {
      setCurrentProject(null);
      onChange(undefined);
    }
    clearSearch();
  };

  const onChangeSearch = useCallback(
    (s: string) => {
      delayedDebounceSearch(s);
      setSearch(s);
    },
    [delayedDebounceSearch, setSearch]
  );

  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  useEffect(() => {
    if (projectUuid) {
      fetchCurrentProject(workspaceSlug, projectUuid);
    } else {
      setCurrentProject(null);
    }
  }, [fetchCurrentProject, setCurrentProject, workspaceSlug, projectUuid]);

  useEffect(() => {
    fetchProjects(workspaceSlug, debounceSearch);
  }, [fetchProjects, workspaceSlug, debounceSearch]);

  const noOptionsMessage = useMemo(() => {
    return ({ inputValue: input }: { inputValue: string }) => {
      if (error) {
        return formatMessage(messages.errorText);
      }
      return input
        ? formatMessage(messages.noSearchResultsText)
        : formatMessage(messages.noProjectOptionsText);
    };
  }, [formatMessage, error]);

  const selectStyle = useMemo(() => {
    const projectFilterWidth = () => {
      if (!currentProject) {
        return gridSize() * 16;
      }
      const characterLength = currentProject.label.length;
      if (characterLength <= 5) {
        return SHORT_PROJECT_SELECTED_WIDTH;
      } else if (characterLength <= 15) {
        return MEDIUM_PROJECT_SELECTED_WIDTH;
      } else {
        return LONG_PROJECT_SELECTED_WIDTH;
      }
    };

    return {
      control: (css: CSSObjectWithLabel, state: filterStyles.StateProps) => ({
        ...css,
        ...filterStyles.subtleSelector(css, state),
        ...selectControlStyles,
        minWidth: projectFilterWidth(),
        maxWidth: gridSize() * 32,
      }),
      menu: (css: CSSObjectWithLabel) => ({
        ...css,
        minWidth: gridSize() * 32,
      }),
      placeholder: (css: CSSObjectWithLabel) => ({
        ...css,
        ...filterStyles.nothingSelectedColor(),
      }),
    };
  }, [currentProject]);

  const handleInputChange = useCallback(
    (inputValue: string, { action }: { action: string }) => {
      if (action === 'input-change') {
        onChangeSearch(inputValue);
      }
    },
    [onChangeSearch]
  );

  const options: (ProjectOption | null)[] = useMemo(() => {
    return projects ? projects.map(mapFilterOption) : [];
  }, [projects]);

  return (
    <SelectField
      inputId="projects-filter"
      value={currentProject}
      isClearable
      options={options}
      isLoading={loading}
      isDisabled={loading}
      formatOptionLabel={formatOptionLabel}
      onChange={handleProjectChange}
      inputValue={search || ''}
      onInputChange={handleInputChange}
      hideSelectedOptions
      placeholder={formatMessage(messages.projectSearchPlaceholder)}
      noOptionsMessage={noOptionsMessage}
      styles={selectStyle}
    />
  );
};
